import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';

import ButtonWidget from './ButtonWidget';
import ImageWidget from './ImageWidget';
import IconWidget from './IconWidget';
import TextWidget from './TextWidget';
import HotspotWidget from './HotspotWidget';
import IframeWidget from './IframeWidget';
import ShapesWidget from './ShapesWidget';
import { getTimelineTimeFromTimeInput, getSecondsFromTimelineTime } from '../../../../../services/timeStampService';
import {
	WIDGET_TYPE_BUTTON,
	WIDGET_TYPE_ICON,
	WIDGET_TYPE_IMAGE,
	WIDGET_TYPE_TEXT,
	WIDGET_TYPE_HOTSPOT,
	WIDGET_TYPE_IFRAME,
	WIDGET_TYPE_SHAPE,
} from '../../../../../constants/scenarioConstant';
import { WidgetProps } from './WidgetProps';

import 'animate.css';
import './WidgetAnimation.scss';

const createWidget = ({
	type,
	...widgetProperties
}: WidgetProps.BaseProps & {
	type?: string;
}) => {
	switch (type) {
		case WIDGET_TYPE_BUTTON:
			return <ButtonWidget {...widgetProperties} />;
		case WIDGET_TYPE_IMAGE:
			return <ImageWidget {...widgetProperties} />;
		case WIDGET_TYPE_ICON:
			return <IconWidget {...widgetProperties} />;
		case WIDGET_TYPE_TEXT:
			return <TextWidget {...widgetProperties} />;
		case WIDGET_TYPE_HOTSPOT:
			return <HotspotWidget {...widgetProperties} />;
		case WIDGET_TYPE_IFRAME:
			return <IframeWidget {...widgetProperties} />;
		case WIDGET_TYPE_SHAPE:
			return <ShapesWidget {...widgetProperties} />;
		default:
			return <></>;
	}
};

type WidgetInteractionItemProps = WidgetProps.BaseProps & {
	data: any;
	bounds: any;
	playerTimePos: number;
	onUpdateLabel?: (_labelContent?: string) => void;
};

const WidgetInteractionItem = ({
	data,
	bounds,
	onClickItem,
	playerTimePos,
	onContextMenu,
	onUpdateLabel,
}: WidgetInteractionItemProps) => {
	const {
		id,
		name,
		widgetTemplateId,
		start,
		end,
		type,
		settings,
		style: { dimension, coordinate, rotation },
		events
	} = data;

	const { x, y } = coordinate ?? { x: 0, y: 0 };
	const { w, h } = dimension ?? { w: 150, h: 100 };

	const itemRef = useRef<HTMLElement | null>(null);

	const isShowWidget =
		playerTimePos >= getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(start)) &&
		playerTimePos <= getSecondsFromTimelineTime(getTimelineTimeFromTimeInput(end));

	const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

	const isEnableWidget = isShowWidget || type === WIDGET_TYPE_IFRAME;
	const exitAnimation = data.style?.transformSettings?.exitAnimation;

	const [isExiting, setIsExiting] = useState(false);
	const [shouldRender, setShouldRender] = useState(isEnableWidget);
	const prevIsShowWidget = useRef(isShowWidget);

	// useLayoutEffect (not useEffect): isShowWidget flips synchronously with
	// render (derived straight from playerTimePos), but isExiting only flips
	// via this effect. With useEffect, the browser can paint the in-between
	// frame where isShowWidget=false and isExiting=false at the same time -
	// every widget's `display: isShowWidget || isExiting ? 'flex' : 'none'`
	// collapses to 'none' for that frame, before the exit CSS class is even
	// applied, so it flashes invisible and only starts animating a frame
	// later. Flushing synchronously before paint closes that gap.
	useLayoutEffect(() => {
		const wasShowing = prevIsShowWidget.current;
		prevIsShowWidget.current = isShowWidget;

		if (!isShowWidget && wasShowing && type !== WIDGET_TYPE_IFRAME) {
			if (exitAnimation) {
				setIsExiting(true);
			} else {
				setShouldRender(false);
			}
		} else if (isShowWidget && !wasShowing) {
			setShouldRender(true);
			setIsExiting(false);
		}
	}, [isShowWidget]);

	const handleExitDone = useCallback(() => {
		setIsExiting(false);
		setShouldRender(false);
	}, []);

	useEffect(() => {
		// Init widget
		if (itemRef.current) {
			const wrapper = itemRef.current as any;
			const children = itemRef.current.querySelector('foreignObject') as any;
			// Update position
			children.setAttribute('x', x);
			children.setAttribute('y', y);

			// Update dimension
			children.setAttribute('width', w);
			children.setAttribute('height', h);

			// Init rotation
			wrapper.style.transform = `rotate(${rotation ?? 0}deg)`;
		}
	}, [bounds, data, isShowWidget, shouldRender]);

	if (data.style && Object.keys(data.style).length === 0) {
		return null;
	}

	const isClickable = events && events.find((event: any) => event.type === 'onClick' && event.actions.length > 0 && (event.actions ?? []).some((a: any) => a.type && a.type !== "none")) ? true : false;
	return (
		shouldRender &&
		createWidget({
			id,
			type,
			isShowWidget,
			isExiting,
			onExitDone: handleExitDone,
			itemRef,
			onClickItem,
			isClickable,
			widgetTemplateId,
			style: data.style,
			name,
			coordinate,
			dimension,
			settings,
			isSafari,
			onContextMenu,
			onUpdateLabel,
		})
	);
};

export default WidgetInteractionItem;
